import React, { FC } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Paper, Typography, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { alpha } from '@material-ui/core/styles';

const BAR_COLOR = '#00A7F7';
const CHART_HEIGHT = 280;
const MIN_BAR_SLOT = 48;

export type PacuMilestoneBarPoint = {
  name: string;
  value: number;
};

const lastNameTick = (name: string) => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/);
  if (parts.length > 1 && ['Jr', 'Sr', 'II', 'III', 'IV', 'V'].includes(parts[parts.length - 1].replace(/[.,]/g, ''))) {
    return parts[parts.length - 2] || name;
  }
  return parts[parts.length - 1] || name;
};

const MilestoneTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) {
    return null;
  }
  const row = payload[0].payload as PacuMilestoneBarPoint;
  const minutes = Math.round(row.value);
  return (
    <Paper>
      <Box p={2}>
        <Typography gutterBottom>{row.name}</Typography>
        <Typography variant="h4">{`${minutes} ${minutes === 1 ? 'min' : 'mins'}`}</Typography>
      </Box>
    </Paper>
  );
};

const PacuMilestoneBarChart: FC<{
  data: PacuMilestoneBarPoint[];
  greenLineY?: number;
}> = ({ data, greenLineY }) => {
  const theme = useTheme();
  const hasThreshold = greenLineY !== undefined && greenLineY !== null && Number.isFinite(greenLineY);
  const maxValue = Math.max(0, ...data.map(point => point.value), hasThreshold ? greenLineY : 0);
  const angled = data.length > 3;
  const needsScroll = data.length > 6;

  if (!data.length) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height={CHART_HEIGHT}
        style={{ color: theme.palette.text.secondary }}
      >
        <Typography variant="body2">No data</Typography>
      </Box>
    );
  }

  return (
    <Box height={CHART_HEIGHT} minWidth={0} overflow="auto hidden">
      <Box width={needsScroll ? Math.max(data.length * MIN_BAR_SLOT, 240) : '100%'} height="100%" minWidth="100%">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 28, right: 8, left: -20, bottom: angled ? 4 : 0 }}>
            <CartesianGrid stroke={alpha(theme.palette.divider, 0.12)} vertical={false} />
            <XAxis
              dataKey="name"
              interval={0}
              angle={angled ? -35 : 0}
              textAnchor={angled ? 'end' : 'middle'}
              height={angled ? 52 : 28}
              tickFormatter={lastNameTick}
              tick={{ fill: 'rgba(255,255,255,0.65)', fontSize: data.length > 10 ? 8 : 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, Math.ceil(maxValue * 1.25) || 1]}
              tickFormatter={value => Math.round(value).toString()}
              tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <Tooltip
              content={({ active, payload }) => <MilestoneTooltip active={active} payload={payload} />}
              cursor={{ fill: 'rgba(255,255,255,0.12)' }}
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 4,
                border: 0,
              }}
            />
            {hasThreshold && <ReferenceLine y={greenLineY} strokeDasharray="3 3" />}
            <Bar dataKey="value" fill={BAR_COLOR} radius={[2, 2, 0, 0]} maxBarSize={48}>
              {data.length <= 16 && (
                <LabelList
                  dataKey="value"
                  position="top"
                  formatter={(value: number) => Math.round(value)}
                  style={{ fill: '#fff', fontSize: 11, fontWeight: 600 }}
                />
              )}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default PacuMilestoneBarChart;
